/**
 * Prises de voix SANS ATTENDRE (Studio « Ta voix » et rubrique « Audios
 * animations ») :
 *
 *   - `RecordingBar` : le prompteur et le micro d'UN texte. « Annuler » pendant
 *     la prise la jette (rien ne part à la transcription) ; « Arrêter » la remet
 *     au parent et se referme aussitôt : on peut enregistrer le texte suivant.
 *   - `useVoiceTakes` : chaque prise est transcrite et déposée EN ARRIÈRE-PLAN
 *     (`animation-voice`), une par texte ; son état (en cours, à réécouter,
 *     refusée) vit ici, par texte.
 *   - `TakeCard` : la prise d'un texte, quand elle est prête : réécouter, voir
 *     ce qui a été entendu, puis « Garder », « Refaire » ou « Jeter », dans
 *     l'ordre qu'on veut.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, Loader2, Mic, RotateCcw, Square, Trash2, Upload, X } from 'lucide-react';
import {
  alignVoice,
  JRAF_PALETTE,
  splitSentences,
  voiceMatch,
  VOICE_MATCH_MIN,
  type VoiceSlot,
} from '@jeraisonne/animation';
import { Button, cn } from '@jeraisonne/ui';
import { useAudioRecorder } from '@/features/exercise-v2-wizard/use-audio-recorder';
import {
  prepareVoiceFile,
  uploadVoice,
  VoiceError,
  VOICE_RECORDER_MIME_TYPES,
  type VoiceUpload,
} from '@/lib/animations/voice';

/** « 12,4 s » */
export function takeSeconds(ms: number): string {
  return `${(ms / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1, minimumFractionDigits: 1 })}\u202fs`;
}

function clock(s: number): string {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export type TakeState =
  | { kind: 'processing'; slot: VoiceSlot }
  | { kind: 'review'; slot: VoiceSlot; take: VoiceUpload; url: string }
  | {
      kind: 'error';
      slot: VoiceSlot;
      message: string;
      /** refusée par la transcription : à réécouter, à garder quand même */
      refused?: { file: File; url: string; heard: string | null };
    };

/** Prises en cours ou prêtes, par texte (`slot.key`). */
export function useVoiceTakes(animationId: string) {
  const [takes, setTakes] = useState<Record<string, TakeState>>({});
  const urls = useRef(new Map<string, string>());
  /** dernière prise envoyée par texte : une ancienne réponse n'écrase pas la nouvelle */
  const latest = useRef(new Map<string, number>());

  const setUrl = (key: string, url: string | null) => {
    const old = urls.current.get(key);
    if (old && old !== url) URL.revokeObjectURL(old);
    if (url) urls.current.set(key, url);
    else urls.current.delete(key);
  };
  useEffect(() => {
    const map = urls.current;
    return () => {
      for (const u of map.values()) URL.revokeObjectURL(u);
      map.clear();
    };
  }, []);

  const send = useCallback(
    async (slot: VoiceSlot, file: File, force: boolean) => {
      const n = (latest.current.get(slot.key) ?? 0) + 1;
      latest.current.set(slot.key, n);
      setTakes((t) => ({ ...t, [slot.key]: { kind: 'processing', slot } }));
      const url = URL.createObjectURL(file);
      let next: TakeState;
      try {
        const take = await uploadVoice({ animationId, file, expected: slot.say, force });
        next = { kind: 'review', slot, take, url };
      } catch (e) {
        next =
          e instanceof VoiceError && e.code === 'no_speech'
            ? { kind: 'error', slot, message: e.message, refused: { file, url, heard: e.heard } }
            : { kind: 'error', slot, message: (e as Error).message };
      }
      if (latest.current.get(slot.key) !== n) {
        URL.revokeObjectURL(url);
        return;
      }
      setUrl(slot.key, url);
      setTakes((t) => (t[slot.key] ? { ...t, [slot.key]: next } : t));
    },
    [animationId],
  );

  /** Une prise (micro ou fichier) : préparée puis envoyée, sans attendre. */
  const submit = useCallback(
    (slot: VoiceSlot, blob: Blob, name?: string) => {
      setTakes((t) => ({ ...t, [slot.key]: { kind: 'processing', slot } }));
      void (async () => {
        let file: File;
        try {
          file = await prepareVoiceFile(blob, name);
        } catch (e) {
          setTakes((t) => ({
            ...t,
            [slot.key]: { kind: 'error', slot, message: (e as Error).message },
          }));
          return;
        }
        await send(slot, file, false);
      })();
    },
    [send],
  );

  /** Prise refusée par la transcription, mais la voix y est : renvoyée en la gardant. */
  const force = useCallback(
    (key: string) => {
      const s = takes[key];
      if (s?.kind === 'error' && s.refused) void send(s.slot, s.refused.file, true);
    },
    [takes, send],
  );

  const discard = useCallback((key: string) => {
    latest.current.set(key, (latest.current.get(key) ?? 0) + 1);
    setUrl(key, null);
    setTakes((t) => {
      const { [key]: _gone, ...rest } = t;
      return rest;
    });
  }, []);

  return { takes, submit, force, discard };
}

/** Prompteur + micro d'un texte. « Arrêter » remet la prise ; « Annuler » la jette. */
export function RecordingBar({
  slot,
  onDone,
  onClose,
}: {
  slot: VoiceSlot;
  /** prise prête à envoyer (le parent l'envoie sans attendre) */
  onDone: (blob: Blob, name?: string) => void;
  onClose: () => void;
}) {
  const rec = useAudioRecorder({ mimeTypes: VOICE_RECORDER_MIME_TYPES });
  const [silent, setSilent] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const micId = useId();
  const sentences = useMemo(() => splitSentences(slot.say), [slot.say]);
  const recording = rec.state === 'recording';

  const start = async () => {
    setSilent(false);
    await rec.start();
  };
  const stop = async () => {
    const { blob, hadSignal } = await rec.stop();
    if (!blob || !hadSignal) {
      setSilent(true);
      return;
    }
    onDone(blob);
  };
  /** pendant la prise : on la jette, rien n'est envoyé */
  const cancel = async () => {
    await rec.stop();
    setSilent(false);
  };

  return (
    <div className="mt-3 space-y-3" aria-label={`Enregistrer : ${slot.label}`} role="group">
      <div
        className={cn(
          'rounded-2xl px-4 py-4 transition-shadow sm:px-6',
          recording && 'ring-error-500 ring-2',
        )}
        style={{ backgroundColor: JRAF_PALETTE.night, color: JRAF_PALETTE.paper }}
      >
        <p className="mb-2 text-[11px] font-bold uppercase tracking-wider opacity-60">
          À dire {recording && <span className="text-[#ff6b6b]">· enregistrement</span>}
        </p>
        <div className="space-y-2 text-[19px] font-medium leading-snug sm:text-[22px]">
          {sentences.map((s, i) => (
            <p key={i}>{s}</p>
          ))}
        </div>
        <p className="mt-3 text-[12px] opacity-60">
          Dis-le à ta façon : le texte est un guide. Les silences du début et de la fin sont coupés.
        </p>
      </div>

      {rec.error && (
        <p role="alert" className="text-error-600 text-[13px]">
          {rec.error}
        </p>
      )}
      {silent && (
        <p role="alert" className="text-error-600 text-[13px] font-semibold">
          Je n’ai rien entendu : vérifie le micro (niveau qui bouge), puis recommence.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {!recording ? (
          <Button onClick={() => void start()} variant="destructive">
            <Mic className="h-4 w-4" aria-hidden /> {silent ? 'Recommencer' : 'Démarrer'}
          </Button>
        ) : (
          <>
            <Button onClick={() => void stop()} variant="destructive">
              <Square className="h-4 w-4 fill-current" aria-hidden /> Arrêter
            </Button>
            <Button variant="outline" onClick={() => void cancel()}>
              <X className="h-4 w-4" aria-hidden /> Annuler la prise
            </Button>
          </>
        )}
        {recording && (
          <span className="text-ink-700 inline-flex items-center gap-2 text-sm font-semibold tabular-nums">
            <span className="bg-error-500 h-2.5 w-2.5 animate-pulse rounded-full" aria-hidden />
            {clock(rec.seconds)}
            <span
              className="bg-ink-100 relative h-2 w-24 overflow-hidden rounded-full"
              role="meter"
              aria-label="Niveau du micro"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(rec.level * 100)}
            >
              <span
                className="bg-success-500 absolute inset-y-0 left-0 rounded-full transition-[width] duration-75"
                style={{ width: `${Math.round(rec.level * 100)}%` }}
              />
            </span>
          </span>
        )}
        {!recording && (
          <>
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
              <Upload className="h-4 w-4" aria-hidden /> Importer un fichier
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept="audio/*,.m4a,.mp3,.wav,.aac"
              className="hidden"
              aria-label="Fichier audio (m4a, mp3, wav)"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) onDone(f, f.name);
              }}
            />
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" aria-hidden /> Fermer
            </Button>
          </>
        )}
        {!recording && rec.devices.length > 1 && (
          <span className="flex items-center gap-1.5">
            <label htmlFor={micId} className="text-ink-600 text-[12px]">
              Micro
            </label>
            <select
              id={micId}
              value={rec.deviceId ?? ''}
              onChange={(e) => rec.setDeviceId(e.target.value || null)}
              className="border-ink-200 h-9 max-w-[14rem] rounded-lg border bg-white px-2 text-[13px]"
            >
              <option value="">Micro du système</option>
              {rec.devices.map((d, i) => (
                <option key={d.deviceId || i} value={d.deviceId}>
                  {d.label || `Micro ${i + 1}`}
                </option>
              ))}
            </select>
          </span>
        )}
      </div>
    </div>
  );
}

/** La prise d'un texte : en cours d'analyse, à réécouter, ou refusée. */
export function TakeCard({
  state,
  saving,
  onKeep,
  onRedo,
  onDiscard,
  onForce,
}: {
  state: TakeState;
  saving: boolean;
  onKeep: (take: VoiceUpload) => void;
  onRedo: () => void;
  onDiscard: () => void;
  onForce: () => void;
}) {
  const sentences = useMemo(() => splitSentences(state.slot.say), [state.slot.say]);
  if (state.kind === 'processing')
    return (
      <p
        role="status"
        className="border-ink-200 text-ink-600 mt-2 flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm"
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        J’écoute la prise… tu peux enregistrer un autre texte en attendant.
      </p>
    );

  if (state.kind === 'error')
    return (
      <div className="border-ink-200 mt-2 space-y-2 rounded-2xl border p-3">
        <p role="alert" className="text-error-600 text-[13px] font-semibold">
          {state.message}
        </p>
        {state.refused && (
          <>
            <audio
              controls
              src={state.refused.url}
              aria-label="Réécouter la prise refusée"
              className="w-full"
            />
            {state.refused.heard && (
              <p className="text-ink-700 text-[13px]">
                <span className="font-semibold">Entendu :</span> « {state.refused.heard} »
              </p>
            )}
          </>
        )}
        <div className="flex flex-wrap gap-2">
          {state.refused && (
            <Button variant="outline" onClick={onForce}>
              <Check className="h-4 w-4" aria-hidden /> Ma voix y est : garder quand même
            </Button>
          )}
          <Button variant="outline" onClick={onRedo}>
            <RotateCcw className="h-4 w-4" aria-hidden /> Refaire
          </Button>
          <Button variant="ghost" onClick={onDiscard}>
            <Trash2 className="h-4 w-4" aria-hidden /> Jeter
          </Button>
        </div>
      </div>
    );

  const fit = alignVoice(sentences, state.take);
  const match = voiceMatch(state.slot, state.take);
  return (
    <div className="border-ink-200 mt-2 space-y-2 rounded-2xl border p-3" data-take="review">
      <audio controls src={state.url} aria-label="Réécouter la prise" className="w-full" />
      <p className="text-ink-700 text-[13px]">
        <span className="font-semibold">Entendu :</span> « {state.take.transcript} »
      </p>
      <p className="text-ink-500 text-[12px]">
        {takeSeconds(state.take.durationMs)}
        {fit &&
          ` · joués : ${takeSeconds(fit.totalMs)} (silences coupés : ${takeSeconds(fit.offsetMs)} au début, ${takeSeconds(
            Math.max(0, state.take.durationMs - fit.offsetMs - fit.totalMs),
          )} à la fin)`}
      </p>
      {match !== null && match < VOICE_MATCH_MIN && (
        <p className="border-warning-500/40 bg-warning-50 text-ink-800 rounded-lg border px-2.5 py-1.5 text-[13px] font-semibold">
          La prise s’écarte du texte ({Math.round(match * 100)} % des mots retrouvés) : les
          sous-titres ne suivront pas bien. Refais-la, ou garde-la si c’est voulu.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => onKeep(state.take)} loading={saving}>
          <Check className="h-4 w-4" aria-hidden /> Garder cette prise
        </Button>
        <Button variant="outline" onClick={onRedo} disabled={saving}>
          <RotateCcw className="h-4 w-4" aria-hidden /> Refaire
        </Button>
        <Button variant="ghost" onClick={onDiscard} disabled={saving}>
          <Trash2 className="h-4 w-4" aria-hidden /> Jeter
        </Button>
      </div>
    </div>
  );
}
